
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/providers/DirectionProvider';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MapPin, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Settlement } from '@/hooks/useSettlements';

interface CitySelectProps {
  city: string;
  cityDisplayName?: string; // Pre-translated city name for display
  settlements: Settlement[];
  searchTerm: string;
  loading: boolean;
  openPopover: boolean;
  setOpenPopover: (open: boolean) => void;
  setSearchTerm: (term: string) => void;
  onSelect: (settlement: Settlement) => void;
}

/**
 * Get settlement display name based on current language
 */
const getSettlementDisplayName = (settlement: Settlement, lang: string): string => {
  if (lang === 'en' && settlement.name_en) return settlement.name_en;
  if (lang === 'fr' && settlement.name_fr) return settlement.name_fr;
  if (lang === 'ru' && settlement.name_ru) return settlement.name_ru;
  return settlement.name; // Fallback to Hebrew
};

const CitySelect: React.FC<CitySelectProps> = ({
  city,
  cityDisplayName,
  settlements,
  searchTerm,
  loading,
  openPopover,
  setOpenPopover,
  setSearchTerm,
  onSelect,
}) => {
  const { t, i18n } = useTranslation();
  const { direction } = useDirection();
  const currentLang = i18n.language;

  // Build settlements with display names and searchable text from all languages
  const displaySettlements = useMemo(() => {
    return settlements.map(settlement => ({
      ...settlement,
      displayName: getSettlementDisplayName(settlement, currentLang),
      // Combine all language variants for searching
      searchValue: [
        settlement.name,
        settlement.name_en,
        settlement.name_fr,
        settlement.name_ru
      ].filter(Boolean).join(' ')
    }));
  }, [settlements, currentLang]);

  // Use provided display name or the city (Hebrew) as fallback
  const displayCity = cityDisplayName || city;

  return (
    <div className="space-y-2">
      <Label htmlFor="location" className="text-gray-700">{t('requestForm.selectCity')}</Label>
      <Popover open={openPopover} onOpenChange={setOpenPopover}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={openPopover} aria-label={t('requestForm.selectCity')} className="w-full justify-between text-right pr-3 pl-10 relative rounded-lg">
            {displayCity || t('requestForm.selectCity')}
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full min-w-[230px] bg-white shadow-lg z-50 p-0 border border-gray-300" align="start" side="bottom" avoidCollisions={false} style={{
          overflow: 'visible'
        }}>
          <Command dir={direction}>
            <CommandInput placeholder={t('requestForm.searchCity')} value={searchTerm} onValueChange={setSearchTerm} className="bg-white" />
            <CommandEmpty>
              {searchTerm.length < 2
                ? t('requestForm.minCharactersCity')
                : loading
                  ? t('common.loading')
                  : t('requestForm.noCityFound')}
            </CommandEmpty>
            <CommandList className="bg-white z-50 max-h-64 overflow-auto border-gray-200">
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin ml-2" />
                  <span className="text-sm text-gray-500">{t('requestForm.searchingCities')}</span>
                </div>
              ) : (
                <CommandGroup>
                  {displaySettlements.map(settlement => (
                    <CommandItem
                      key={settlement.name}
                      value={settlement.searchValue}
                      onSelect={() => {
                        onSelect(settlement);
                        setSearchTerm('');
                        setOpenPopover(false);
                      }}
                    >
                      {settlement.displayName}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default CitySelect;
